/**
 * Report-funnel handoff (Lane B).
 *
 * The report funnel (jyotishpuraan.com/report) sends a paid buyer to
 *   https://app.jyotishpuraan.com/reports?flow=report&report_id={id}
 * to read what they just bought. Guest checkout creates/reuses a user
 * keyed on the checkout phone (report_checkout.py find-or-create by
 * normalize_phone), and the content endpoint is owner-scoped — so a guest
 * has to log in with THAT number before the report will open.
 *
 * That means the target report_id has to survive a full OTP round-trip.
 * This module is the single place that knows how it is stashed, so
 * ReportsPage (which writes it) and LoginPage (which honours it on the way
 * back) cannot disagree about the key or the shape.
 */

const HANDOFF_KEY = 'jp_report_handoff';

/** Reads ?flow=report&report_id=… off a search string. */
export function parseReportFlow(search) {
  try {
    const params = new URLSearchParams(search || '');
    const flow = (params.get('flow') || '').toLowerCase();
    const reportId = (params.get('report_id') || '').trim();
    if (flow !== 'report' || !reportId) return null;
    return { reportId };
  } catch {
    return null;
  }
}

/**
 * Remember the report the buyer is trying to reach, and where to come back
 * to, before sending them through login.
 *
 * Also clears `jp_entry_intent`: LoginPage stashes that from funnel URLs
 * (?intent=marriage etc.) and PremiumDashboard later consumes it to
 * auto-route into a ChitraGupt session. A stale value from an earlier
 * funnel visit would hijack this handoff, so a report arrival drops it.
 */
export function stashReportHandoff(reportId) {
  if (!reportId) return;
  try {
    sessionStorage.setItem(HANDOFF_KEY, JSON.stringify({ report_id: reportId }));
    sessionStorage.setItem(
      'authReturnPath',
      `/reports?flow=report&report_id=${encodeURIComponent(reportId)}`
    );
    sessionStorage.removeItem('jp_entry_intent');
  } catch {
    // Private mode / storage disabled — the handoff degrades to the plain
    // Library, which is still a working (if less direct) destination.
  }
}

/** The pending handoff, or null. Does not consume it. */
export function peekReportHandoff() {
  try {
    const raw = sessionStorage.getItem(HANDOFF_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    const reportId = (parsed?.report_id || '').trim();
    return reportId ? { reportId } : null;
  } catch {
    return null;
  }
}

/** Forget the handoff, so a refresh cannot re-trigger it. */
export function clearReportHandoff() {
  try {
    sessionStorage.removeItem(HANDOFF_KEY);
  } catch { /* no-op */ }
}
